import { ROUTES } from 'src/router/const'
import {
  PRACTICE_FOLLOWED_TICKET_ID,
  PRACTICE_STAGE,
} from 'src/services/consts/onboarding-practice.const'
import { TICKET_CREATION_STEP_NAMES } from 'src/services/consts/ticket-creation.const'
import { DIRECTORIES } from 'src/services/consts/ticket.const'

const TOUR_NAV_PREFIX = 'nav-'

// Values for the `data-tour` attribute. The walkthrough story and the highlighted elements share
// these, so a renamed target updates both.
export const TOUR_ANCHORS = {
  DASHBOARD_REQUEST: 'dashboard-request',
  PRODUCT_SEARCH_HEADER: 'product-search-header',
  QUESTION_FIRST: 'question-first',
  REQUEST_FOLDERS: 'request-folders',
  REQUEST_DETAILS_HEADER: 'request-details-header',
  REQUEST_DETAILS_TITLE: 'request-details-title',
  REQUEST_DETAILS_TABS: 'request-details-tabs',
  DRAWER_REQUEST_CTA: 'drawer-request-cta',
  NAV_APPROVALS: `${TOUR_NAV_PREFIX}${ROUTES.APPROVALS}`,
  NAV_CONFIRMATIONS: `${TOUR_NAV_PREFIX}${ROUTES.CONFIRMATIONS}`,
  NAV_SETTINGS: `${TOUR_NAV_PREFIX}${ROUTES.SETTINGS}`,
  NAV_FAQ: `${TOUR_NAV_PREFIX}${ROUTES.FAQ}`,
}

// The drawer links the walkthrough highlights, keyed by route name. The drawer reads this map; a
// link that is not in it gets no `data-tour`.
export const TOUR_NAV_ANCHORS = {
  [ROUTES.APPROVALS]: TOUR_ANCHORS.NAV_APPROVALS,
  [ROUTES.CONFIRMATIONS]: TOUR_ANCHORS.NAV_CONFIRMATIONS,
  [ROUTES.SETTINGS]: TOUR_ANCHORS.NAV_SETTINGS,
  [ROUTES.FAQ]: TOUR_ANCHORS.NAV_FAQ,
}

// The walkthrough story, in order. Each card names its targets in `anchors`, a list of `TOUR_ANCHORS`
// values. An empty list means a centred card with no cut-out, one item highlights one control, and
// two items highlight their union box. Each card also has its icon, its copy, the placement of the
// card beside its highlight, and the screen it needs (`scene`). A card whose targets sit against
// their neighbours sets `padding: 0`, so the cut-out stays off the control next door. Cards 1 and 2 are on the Dashboard,
// cards 3 and 4 walk through the request wizard, cards 5 to 8 follow the request and the menu, and
// card 9 returns to the employee's real Dashboard.
export const ONBOARDING_STORY = [
  {
    id: 'intro',
    anchors: [],
    icon: 'compass',
    title: 'Welcome',
    description:
      "This is our organization's AI Governance platform. Here's a quick tour on what you can do here, we will use example product requests to guide you.",
    placement: 'right',
    scene: { route: ROUTES.HOME, stage: PRACTICE_STAGE.NONE },
  },
  {
    id: 'start-request',
    anchors: [TOUR_ANCHORS.DRAWER_REQUEST_CTA],
    icon: 'plus',
    title: 'How do you get started?',
    description:
      'New AI product requests start with Request approval, the plus button in the menu. Next, follow an example AI product request from search to review.',
    placement: 'right-end',
    scene: { route: ROUTES.HOME, stage: PRACTICE_STAGE.NONE },
  },
  {
    id: 'find-tool',
    anchors: [TOUR_ANCHORS.PRODUCT_SEARCH_HEADER],
    icon: 'package-search',
    title: 'Find the tool you need',
    description:
      "First, pick the tool from the directory. If it isn't listed, add it with Custom Entry and it goes through the same review.",
    placement: 'right-start',
    scene: {
      route: ROUTES.TICKET_EDIT,
      params: { ticketStep: TICKET_CREATION_STEP_NAMES.PRODUCT_SEARCH },
      stage: PRACTICE_STAGE.SELECTED,
    },
  },
  {
    id: 'explain-need',
    anchors: [TOUR_ANCHORS.QUESTION_FIRST],
    icon: 'file-text',
    title: 'Explain why you need it',
    description:
      "Your answers and files are what the administrator reviews. Once you submit them, the request can't be edited.",
    placement: 'right-start',
    scene: {
      route: ROUTES.TICKET_EDIT,
      params: {
        ticketStep: TICKET_CREATION_STEP_NAMES.REQUEST_DETAILS,
        ticketId: PRACTICE_FOLLOWED_TICKET_ID,
      },
      stage: PRACTICE_STAGE.DRAFT,
    },
  },
  {
    id: 'track-decision',
    anchors: [TOUR_ANCHORS.REQUEST_FOLDERS],
    icon: 'clipboard-list',
    title: 'Requests are filed by status',
    description:
      'Submitted requests wait in Under review until the administrator moves them to Approved or Denied. Unfinished ones wait in Draft.',
    placement: 'right-start',
    scene: {
      route: ROUTES.TICKETS,
      query: { status: DIRECTORIES.inReview },
      stage: PRACTICE_STAGE.SUBMITTED,
    },
  },
  {
    id: 'stay-in-loop',
    anchors: [
      TOUR_ANCHORS.REQUEST_DETAILS_HEADER,
      TOUR_ANCHORS.REQUEST_DETAILS_TITLE,
      TOUR_ANCHORS.REQUEST_DETAILS_TABS,
    ],
    icon: 'eye',
    title: 'Stay in the loop',
    description:
      "Here you can check the status, review your answers, add files, and message the administrator, for example to withdraw a request. They may ask you questions, and you'll be notified when they reply or decide.",
    placement: 'left',
    // The header fills the drawer, so no padding: it would reach past the drawer's edge.
    padding: 0,
    scene: {
      route: ROUTES.TICKETS,
      query: { status: DIRECTORIES.inReview, ticketId: PRACTICE_FOLLOWED_TICKET_ID },
      stage: PRACTICE_STAGE.SUBMITTED,
    },
  },
  {
    id: 'track-access',
    anchors: [TOUR_ANCHORS.NAV_APPROVALS, TOUR_ANCHORS.NAV_CONFIRMATIONS],
    icon: 'badge-check',
    title: 'Keep track of your access',
    description:
      'Approved tools appear in Product approvals, where you can revoke any you no longer need. Some access needs confirming again from time to time, in Product confirmations.',
    placement: 'right',
    // Menu items sit 4px apart, so no padding: it would catch the item above.
    padding: 0,
    scene: {
      route: ROUTES.TICKETS,
      query: { status: DIRECTORIES.inReview },
      stage: PRACTICE_STAGE.SUBMITTED,
    },
  },
  {
    id: 'settings-help',
    anchors: [TOUR_ANCHORS.NAV_SETTINGS, TOUR_ANCHORS.NAV_FAQ],
    icon: 'settings',
    title: 'Settings and help',
    description:
      'If our organization uses Slack, connect it in Settings to get notifications without opening the portal. Help Center answers common questions and can replay this tour.',
    placement: 'right',
    padding: 0,
    scene: {
      route: ROUTES.TICKETS,
      query: { status: DIRECTORIES.inReview },
      stage: PRACTICE_STAGE.SUBMITTED,
    },
  },
  {
    id: 'ready-dashboard',
    anchors: [TOUR_ANCHORS.DASHBOARD_REQUEST],
    icon: 'sparkles',
    title: "You're ready",
    description:
      'That was example data, so nothing was saved. Your own data is back, and when you need a tool, start with Request Approval.',
    placement: 'bottom',
    scene: { route: ROUTES.HOME, stage: PRACTICE_STAGE.SUBMITTED, isRealData: true },
  },
]

// A card's target gets this long to appear with a steady box. After that the card is skipped.
export const ONBOARDING_TARGET_WAIT_MS = 3000

// The run's states. The tour store holds one, and the actions store moves it. Never a raw string.
export const TOUR_STATUS = {
  IDLE: 'idle', // no run
  CHANGING: 'changing', // a screen is being prepared; the card is hidden and the dim is up
  MOVING: 'moving', // the card is up while the next card's target on the same screen is awaited
  SHOWING: 'showing', // a card is up
  ENDING: 'ending', // the cleanup runs
}
